/**
 * Closed list of audited actions (FRONTEND_REFERENCE.md section 13).
 * Every entry here is a real action the Gateway can attribute to an
 * `Actor` and persist in `audit_log` via `recordAuditEntry` (@mc/db).
 *
 * This enum was referenced (AuditAction.CONFIG_UPDATE, .SERVER_START, etc)
 * by services/gateway from the moment routes/admin.ts and
 * middleware/audit.ts were written, but never actually defined in this
 * package: the gateway did not compile before this file existed. Values
 * below are taken directly from FR section 13's action list, plus
 * CONFIG_UPDATE (system-level runtime_config write, PR section 6.1,
 * distinct from CONFIG_WRITE which is per-server Minecraft config, see
 * routes/admin.ts's own comment on that distinction) and PROFILE_UPDATE
 * (flagged below, not in FR's literal list).
 */
export const AuditAction = {
    SERVER_CREATE: "server.create",
    SERVER_DELETE: "server.delete",
    SERVER_START: "server.start",
    SERVER_STOP: "server.stop",
    SERVER_RESTART: "server.restart",
    SERVER_KILL: "server.kill",
    FILE_UPLOAD: "file.upload",
    FILE_DELETE: "file.delete",
    FILE_EDIT: "file.edit",
    MOD_INSTALL: "mod.install",
    MOD_DELETE: "mod.delete",
    MOD_UPDATE: "mod.update",
    CONFIG_WRITE: "config.write",
    /** System-level runtime_config write (PR section 6.1), distinct from CONFIG_WRITE above. */
    CONFIG_UPDATE: "config.update",
    BACKUP_CREATE: "backup.create",
    BACKUP_RESTORE: "backup.restore",
    BACKUP_DELETE: "backup.delete",
    PLAYER_KICK: "player.kick",
    PLAYER_BAN: "player.ban",
    PLAYER_UNBAN: "player.unban",
    PLAYER_OP: "player.op",
    PLAYER_DEOP: "player.deop",
    PLAYER_WHITELIST_ADD: "player.whitelist_add",
    PLAYER_WHITELIST_REMOVE: "player.whitelist_remove",
    ROLE_CREATE: "role.create",
    ROLE_DELETE: "role.delete",
    ROLE_ASSIGN: "role.assign",
    ROLE_REVOKE: "role.revoke",
    FAMILY_CREATE: "family.create",
    FAMILY_DELETE: "family.delete",
    FAMILY_MEMBER_ADD: "family.member_add",
    FAMILY_MEMBER_REMOVE: "family.member_remove",
    SERVER_FAMILY_ADD: "server.family_add",
    SERVER_FAMILY_REMOVE: "server.family_remove",
    SETTINGS_WRITE: "settings.write",
    EXCLUSIVE_MODE_TOGGLE: "exclusive_mode.toggle",
    AUTH_LOGIN: "auth.login",
    AUTH_LOGOUT: "auth.logout",
    AUTH_REGISTER: "auth.register",
    /** NOT in FR section 13's original closed list. Flagged here rather than
     *  silently folded in: /profile (FR section 12) needs some AuditAction
     *  once it writes to `users`, and this follows the exact same pattern as
     *  SETTINGS_WRITE for a server, but Architect should confirm this belongs
     *  in the closed list rather than Implementer extending it unilaterally. */
    PROFILE_UPDATE: "profile.update",
    /** NOT in FR section 13's original closed list either, same flagged-extension
     *  pattern as PROFILE_UPDATE directly above. Consola en vivo (FR section 19
     *  item 6) sends arbitrary commands to a running Minecraft server via
     *  CraftyClient.sendStdin — auditing what was actually typed (not just that
     *  someone had console.write) seemed too consequential to leave unaudited
     *  by omission, but Architect should confirm the same way. */
    CONSOLE_COMMAND: "console.command",
};
//# sourceMappingURL=auditActions.js.map